import { Box, Typography, styled, Button, Grid, Stack } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import MyDialog from '../../components/dialog-component/my-dialog';
import { dispatch, useSelector } from '../../redux/store';
import useLocales from '../../hooks/useLocales';
import useAuth from '../../hooks/useAuth';
import { fNumberDecimal } from '../../utils/formatNumber';
import { getUserMemberBalance } from '../../redux/slices/lookup';
import conObj, { showResponseError } from '../../utils/connection-assistant';
import Iconify from '../../components/Iconify';
import { openConfirm } from '../../redux/slices/confirm';
import { openLogin } from '../../redux/slices/authUiSlice';

const Balance = ({ handleOpen, handleClose }) => {
  const dispatchUi = useDispatch();
  const navigate = useNavigate();
  const { translate } = useLocales();
  const { isAuthenticated, userProfile } = useAuth();
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  // 1. Redux state
  const { memberBalance, siteSettings, withdrawRequirement, depositWithdrawMinMax, memberBankInfo } = useSelector(
    (x) => ({
      ...x.lookup,
    })
  );
  const { enqueueSnackbar } = useSnackbar();
  // 2. Logic state for dynamic limits
  const [minWithdrawal, setMinWithdrawal] = useState(0);
  const [maxWithdrawal, setMaxWithdrawl] = useState(0);
  const [minDeposit, setMinDeposit] = useState(0);
  // 3. Calculation logic from HomeBalance
  const isDisplayCalculation = useMemo(
    () =>
      (withdrawRequirement?.calculation_type === 'turnover' || withdrawRequirement?.calculation_type === 'winover') &&
      withdrawRequirement?.requirement !== 0,
    [withdrawRequirement?.calculation_type, withdrawRequirement?.requirement]
  );

  useEffect(() => {
    if (handleOpen && isAuthenticated) {
      setIsLoadingBalance(true);
      getUserMemberBalance().finally(() => setIsLoadingBalance(false));
    }
  }, [handleOpen, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      getMinMaxWithdrawal(depositWithdrawMinMax);
      getMinDeposit(depositWithdrawMinMax);
    } else {
      const currencyTopupValues = Object.values(siteSettings?.currencyTopup || {});
      const currencyWithdrawValues = Object.values(siteSettings?.currencyWithdraw || {});

      if (currencyTopupValues.length > 0) {
        setMinDeposit(currencyTopupValues[0]);
      }

      if (currencyWithdrawValues.length > 0) {
        setMinWithdrawal(currencyWithdrawValues[0]);
        setMaxWithdrawl(currencyWithdrawValues[currencyWithdrawValues.length - 1]);
      }
    }
  }, [isAuthenticated, depositWithdrawMinMax]);

  const clearBalance = () =>
    // if (memberBalance?.balance < 1) {
    conObj
      .post('/members/clear_balance.json')
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data.status === 1) {
            enqueueSnackbar(res?.data?.data?.msg, {
              variant: 'success',
              autoHideDuration: 5000,
            });
            getUserMemberBalance();
          } else {
            enqueueSnackbar(res?.data?.data?.msg, {
              variant: 'error',
              autoHideDuration: 5000,
            });
          }
        }
      })
      .catch((err) => {
        showResponseError(err, enqueueSnackbar);
      });

  const getMinMaxWithdrawal = (data) => {
    if (data) {
      if (data.minWithdrawLimit > 0) {
        setMinWithdrawal(fNumberDecimal(data.minWithdrawLimit));
      } else {
        const currencyWithdrawValues = Object.values(data?.currencyWithdraw || {});
        setMinWithdrawal(fNumberDecimal(currencyWithdrawValues[0]));
      }

      if (data.maxWithdrawLimit > 0) {
        setMaxWithdrawl(fNumberDecimal(data.maxWithdrawLimit));
      } else {
        const currencyWithdrawValues = Object.values(data?.currencyWithdraw || {});
        setMaxWithdrawl(fNumberDecimal(currencyWithdrawValues[currencyWithdrawValues?.length - 1]));
      }
    }
  };

  const getMinDeposit = (data) => {
    if (data) {
      if (Object.values(data.currencyTopup).length > 0) {
        setMinDeposit(Object.values(data.currencyTopup)[0]);
      }
    }
  };
  return (
    <MyDialog open={handleOpen} close={handleClose} maxSize={'sm'}>
      {isAuthenticated ? (
        <>
          <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 'bold', textAlign: 'center' }}>
            {`${translate('name')}: ${userProfile?.full_name}`}
          </Typography>

          {memberBankInfo?.bank && (
            <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 'bold', textAlign: 'center' }}>
              {`${memberBankInfo.bank} (${memberBankInfo.bankAccountNumber})`}
            </Typography>
          )}
        </>
      ) : null}

      <Box sx={{ textAlign: 'center', padding: 4 }}>
        {/* Balance Display */}
        <Typography variant="h4" color="primary" sx={{ mb: 4 }}>
          {translate('balance')}:{' '}
          <strong>
            {`${memberBalance?.currencyCode} ${
              memberBalance?.displayBalance ? fNumberDecimal(memberBalance?.displayBalance) : 0
            }`}
          </strong>
        </Typography>

        {/* Action Buttons */}
        <Grid container spacing={2} justifyContent="center" sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="warning"
              fullWidth
              onClick={() => {
                navigate('/user/wallet?tab=deposit');
                handleClose();
              }}
              sx={{
                background: 'linear-gradient(180deg, #FDD146 0%, #F09F0E 100%)',
                color: '#000',
                fontWeight: 700,
                borderRadius: 2,
                py: 1,
                '&:hover': {
                  background: 'linear-gradient(180deg, #F09F0E 0%, #E08F00 100%)',
                },
              }}
            >
              {translate('deposit')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="info"
              fullWidth
              onClick={() => {
                navigate('/user/wallet?tab=withdraw');
                handleClose();
              }}
              sx={{
                background: 'linear-gradient(180deg, #FDD146 0%, #F09F0E 100%)',
                color: '#000',
                fontWeight: 700,
                borderRadius: 2,
                py: 1,
                '&:hover': {
                  background: 'linear-gradient(180deg, #F09F0E 0%, #E08F00 100%)',
                },
              }}
            >
              {translate('withdraw')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              fullWidth
              disabled={isLoadingBalance}
              onClick={() => {
                if (isAuthenticated && !isLoadingBalance) {
                  setIsLoadingBalance(true);
                  getUserMemberBalance().finally(() => setIsLoadingBalance(false));
                }
              }}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, py: 1 }}
            >
              {isLoadingBalance ? '...' : translate('refresh')}
            </Button>
          </Grid>
        </Grid>

        {/* Info Section with Integrated Logic */}
        <Stack spacing={1.5} sx={{ mt: 2, px: { xs: 0, sm: 4 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              pb: 1,
            }}
          >
            <Typography variant="body2" color="white">
              {translate('minimum_deposit')}
            </Typography>
            <Typography variant="body2" color="white">
              <strong>
                {' '}
                {isAuthenticated ? memberBalance?.currencyCode : process.env.REACT_APP_CURRENCY_CODE} {minDeposit}
              </strong>
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              pb: 1,
            }}
          >
            <Typography variant="body2" color="white">
              {translate('minimum_withdraw')}
            </Typography>
            <Typography variant="body2" color="white">
              <strong>
                {' '}
                {isAuthenticated ? memberBalance?.currencyCode : process.env.REACT_APP_CURRENCY_CODE} {minWithdrawal}
              </strong>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="white">
              {translate('maximum_withdraw')}
            </Typography>
            <Typography variant="body2" color="white">
              <strong>
                {isAuthenticated ? memberBalance?.currencyCode : process.env.REACT_APP_CURRENCY_CODE} {maxWithdrawal}
              </strong>
            </Typography>
          </Box>

          {isDisplayCalculation && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, fontStyle: 'italic' }}>
              *{translate('min_max_withdraw_will_reset')}
            </Typography>
          )}
          {memberBalance.canClearBalance && (
            <Button
              variant="outlined"
              fullWidth
              sx={{
                mt: 4,
                color: '#ff6b6b',
                borderColor: 'rgba(255, 107, 107, 0.5)',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 500,
                backdropFilter: 'blur(6px)',
                background: 'rgba(255, 107, 107, 0.05)',
                transition: 'all 0.25s ease',
                '&:hover': {
                  borderColor: '#ff6b6b',
                  background: 'rgba(255, 107, 107, 0.12)',
                },
              }}
              onClick={() => {
                if (isAuthenticated) {
                  dispatch(
                    openConfirm({
                      title: translate('clear_balance'),
                      subTitle: translate('this_will_set_balance'),
                      onConfirm: () => {
                        clearBalance();
                        setIsLoadingBalance(true);
                        getUserMemberBalance().finally(() => setIsLoadingBalance(false));
                      },
                    })
                  );
                } else {
                  dispatch(openLogin());
                }
              }}
            >
              <Typography fontSize={12} fontWeight={300} sx={{ cursor: 'pointer' }}>
                {translate('clear_balance')}
              </Typography>
            </Button>
          )}
        </Stack>
      </Box>
    </MyDialog>
  );
};

export default Balance;
